import { useCallback, useState } from "react";
import { GridCellSelectionModel, useGridApiRef, GridRowId } from "@mui/x-data-grid-premium";

export interface BulkDeletionConfig {
  canEdit: boolean;
  tableRows: any[];
  tableColumns: any[];
  onRowUpdate: (updatedRow: any, originalRow: any) => any;
  onActionResult?: (result: { type: "error" | "info" | "success" | "warning"; message: string }) => void;
  onPermissionAlert?: () => void;
  getIsDeletedRow?: (rowId: GridRowId) => boolean;
  idField?: string; // Field name for row ID, defaults to 'Ky'
}

export function useBulkCellDeletion(config: BulkDeletionConfig) {
  const {
    canEdit,
    tableRows,
    tableColumns,
    onRowUpdate,
    onActionResult,
    onPermissionAlert,
    getIsDeletedRow,
    idField = 'Ky'
  } = config;

  const apiRef = useGridApiRef();
  const [cellSelectionModel, setCellSelectionModel] = useState<GridCellSelectionModel>({});

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Check if delete or backspace key was pressed
    if (event.key === 'Delete' || event.key === 'Backspace') {
      
      // Check if any cell is currently being edited by looking for editing elements
      const editingCell = document.querySelector('.MuiDataGrid-cell--editing');
      if (editingCell) {
        // A cell is being edited, let the normal editing behavior handle backspace/delete
        return;
      }

      // Check if we have a text selection or the target is an input/textarea
      const target = event.target as HTMLElement;
      if (target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true' ||
        target.closest('.MuiDataGrid-cell--editing')
      )) {
        // Don't intercept if user is typing in an input or editable element
        return;
      }
      
      // Prevent default behavior only if we're going to handle it
      event.preventDefault();
      event.stopPropagation();
      
      // Check if we have selected cells and can edit
      if (!canEdit || Object.keys(cellSelectionModel).length === 0) {
        if (!canEdit && onPermissionAlert) {
          onPermissionAlert();
        }
        return;
      }

      // Process all selected cells
      const updatedRows: { [rowId: string]: any } = {};
      
      Object.entries(cellSelectionModel).forEach(([rowId, selectedFields]) => {
        if (selectedFields && Object.keys(selectedFields).length > 0) {
          const currentRow = tableRows.find(row => row[idField].toString() === rowId);
          if (currentRow && (!getIsDeletedRow || !getIsDeletedRow(currentRow[idField]))) {
            // Create updated row with cleared values for selected fields
            const updatedRow = { ...currentRow };
            Object.keys(selectedFields).forEach(fieldName => {
              // Only clear editable fields that are not readonly
              const column = tableColumns.find(col => col.accessor === fieldName);
              if (column && !column.readonly && fieldName !== idField) {
                updatedRow[fieldName] = null;
              }
            });
            updatedRows[rowId] = { updated: updatedRow, original: currentRow };
          }
        }
      });

      // Apply all updates together to maintain consistent state
      if (Object.keys(updatedRows).length > 0) {
        const updates = Object.values(updatedRows);
        
        // Process all updates immediately in a batch
        updates.forEach(({ updated, original }) => {
          onRowUpdate(updated, original);
        });

        if (onActionResult) {
          const clearedCount = Object.keys(updatedRows).length;
          onActionResult({
            type: "info",
            message: `${clearedCount} row${clearedCount > 1 ? 's' : ''} updated - selected cells cleared`
          });
        }
      }
    }
  }, [cellSelectionModel, canEdit, tableRows, tableColumns, getIsDeletedRow, onRowUpdate, onActionResult, onPermissionAlert, idField, apiRef]);

  const handleCellSelectionModelChange = useCallback((newModel: GridCellSelectionModel) => {
    setCellSelectionModel(newModel);
  }, []);

  return {
    apiRef,
    cellSelectionModel,
    handleKeyDown,
    handleCellSelectionModelChange
  };
}
